/**
 * Coverage-related data fetchers
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createOrUpdateCoverage } from "@/src/actions/coverage";
import { toast } from "@/src/hooks/use-toast";
import { INSURANCE_CARD_CACHE_KEY } from "@/src/lib/data-fetchers/insurance-card-fetchers";
import { PATIENTS_CACHE_KEY } from "@/src/lib/data-fetchers/patients-fetchers";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASK_CACHE_KEY, TASKS_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Coverage } from "@/src/models/coverage";
import { Patient } from "@/src/models/patient";

function patchPatientCoverages(patient: Patient, savedCoverage: Coverage): Patient {
  if (patient.id !== savedCoverage.patientId) {
    return patient;
  }

  const coverages = patient.coverages ?? [];
  const exists = coverages.some((coverage) => coverage.id === savedCoverage.id);

  return {
    ...patient,
    coverages: exists
      ? coverages.map((coverage) => (coverage.id === savedCoverage.id ? { ...coverage, ...savedCoverage } : coverage))
      : [...coverages, savedCoverage],
  };
}

export function useCoverageUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (coverage: Coverage) => {
      return await createOrUpdateCoverage(coverage, true);
    },
    onSuccess: (savedCoverage) => {
      queryClient.setQueriesData({ queryKey: [PATIENTS_CACHE_KEY] }, (data: unknown) => {
        if (Array.isArray(data)) {
          return (data as Patient[]).map((patient) => patchPatientCoverages(patient, savedCoverage));
        }

        if (data && typeof data === "object" && "id" in data) {
          return patchPatientCoverages(data as Patient, savedCoverage);
        }

        return data;
      });
    },
    onError: (error) => {
      logger.error("Error updating coverage:", { error });

      toast({
        title: "Error updating coverage",
        description: "There was an error saving the coverage information. Please try again.",
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [
        [PATIENTS_CACHE_KEY],
        [INSURANCE_CARD_CACHE_KEY],
        [REFERRALS_CACHE_KEY],
        [TASKS_CACHE_KEY],
        [TASK_CACHE_KEY],
      ],
    },
  });
}

/**
 * Fetch the top insurance payers
 */
export function useInsuranceTopPayers() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["insurance-top-payers"],
    queryFn: async () => {
      const res = await fetch("/api/insurance-top-payers");

      if (!res.ok) throw new Error("Failed to fetch insurance top payers");

      return res.json();
    },
    staleTime: Infinity,
  });

  return {
    topInsurancePayers: data || [],
    isLoading,
    isError,
  };
}
